import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { errorStatus, isNotFound } from '../lib/httpError';
import { useI18n } from '../i18n';
import { useAuthStore } from '../store/auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { getOfficial } from '../api/client';
import AthleteAvatar from '../components/shared/AthleteAvatar';
import BackLink from '../components/shared/BackLink';
import OfficialTimeline from '../components/officials/OfficialTimeline';
import OfficialProfile from '../components/officials/OfficialProfile';
import { OwnTeamTable, PanelTable, SectionTitle, StatTile } from '../components/officials/QualityTables';
import BoutLevelPill, { levelLabel } from '../components/bout/BoutLevelPill';
import BoutHistoryList, { outcomeWithRound } from '../components/bout/BoutHistoryList';
import NotFoundState from '../components/shared/NotFoundState';
import ErrorState from '../components/shared/ErrorState';
import Skeleton from '../components/shared/Skeleton';
import { ScaleIcon, PeopleIcon, ClipboardIcon, DotIcon, WarningIcon, CheckIcon } from '../components/shared/Icons';
import { personName } from '../utils/personName';
import { logBackgroundError } from '../observability';
import { useLoad } from '../hooks/useLoad';
import { ALL_OFFICIAL_STATS_ROLES, ownTeamAgainstJury, ownTeamRounds, panelCompared, panelWith, rate } from '../utils/officialStats';
import type { BoutLevel, OfficialBoutQuality, OfficialLevels, OfficialSeat } from '../types';

/** #641 — a rate shows from this many compared cards; below it, the page
 *  shows the count and the total only (operator decision 6, 2026-10-22). */
const MIN_RATE = 30;

/** #616 — the page of one official: the counts of the seats and the history
 *  of the counted bouts (scored, not a Walk Over), in the row shape of the
 *  athlete page (#615). The own page of any user; every page for
 *  canSeeAllOfficialStats (admin, tournament_manager, jury). */
export default function OfficialDetail() {
  const { id: userId } = useParams<{ id: string }>();
  const { t } = useI18n();
  const { user, hasRole } = useAuthStore();
  const canSeeAllOfficialStats = hasRole(...ALL_OFFICIAL_STATS_ROLES);
  // No person name in a title: the title reaches the telemetry (#452).
  usePageTitle(t.officials.title);

  // #665, F02 — useLoad holds the loading, the error, Retry and the refresh.
  // A 404 and a 403 are answers, not failures: they get no telemetry.
  const page = useLoad(
    userId ? () => getOfficial(userId).then(r => r.data) : null,
    [user?.id, userId],
    { context: 'OfficialDetail:load', expected: err => isNotFound(err) || errorStatus(err) === 403 },
  );
  const { data, setData, reload: load } = page;
  const forbidden = errorStatus(page.error) === 403;
  const notFound = !!page.error && (isNotFound(page.error) || forbidden);

  // #641, #642 — a profile write changes the own-team numbers and the marks of
  // the bout rows too (they read the team history): read the page again, with
  // no loading state. Only the newest read draws, so a late answer never
  // draws over a newer write.
  const refresh = () => { page.refresh().catch(logBackgroundError('OfficialDetail:refresh')); };

  if (!user) return null;
  const back = canSeeAllOfficialStats ? <BackLink to="/officials" label={t.officials.title} /> : null;
  if (page.loading || !userId) return <div className="space-y-4">{back}<Skeleton.List shape="section" count={3} /></div>;
  if (notFound) {
    return <div className="space-y-4">{back}<NotFoundState icon={<ScaleIcon className="w-12 h-12" />}
      title={forbidden ? t.errors.OFFICIAL_STATS_FORBIDDEN : t.errors.USER_NOT_FOUND} to="/" linkLabel={t.nav.home} /></div>;
  }
  if (page.error || !data) return <div className="space-y-4">{back}<ErrorState onRetry={load} /></div>;

  const { official, counts, bouts } = data;
  const seatLabel: Record<OfficialSeat, string> = { ring_referee: t.bout.ringReferee, side_judge: t.bout.sideJudge, jury: t.bout.jury };
  const seatIcon: Record<OfficialSeat, ReactNode> = {
    ring_referee: <ScaleIcon className="w-3.5 h-3.5" />, side_judge: <PeopleIcon className="w-3.5 h-3.5" />, jury: <ClipboardIcon className="w-3.5 h-3.5" />,
  };
  const tiles: [string, string, number][] = [
    ['tournaments', t.officials.tournaments, counts.tournaments],
    ['ring_referee', t.bout.ringReferee, counts.ring_referee],
    ['side_judge', t.bout.sideJudge, counts.side_judge],
    ['jury', t.bout.jury, counts.jury],
  ];

  return (
    <div className="space-y-4">
      {back}
      <div className="card p-4 flex items-center gap-4">
        <AthleteAvatar firstName={official.first_name || '?'} lastName={official.last_name || '?'} photoUrl={official.photo_url} size="lg" />
        <div className="min-w-0">
          <h1 data-testid="official-name" className="font-bold text-lg break-words">{personName(official.first_name, official.last_name)}</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="official-counts">
        {tiles.map(([key, label, n]) => (
          <div key={key} className="card p-3 text-center" data-testid={`official-count-${key}`}>
            <div className="text-2xl font-black">{n}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* #622 — the level of the counted bouts (backend lib/boutLevel.ts),
          overall and for each seat. Every viewer of the page sees it. */}
      {bouts.length > 0 && (() => {
        const L = data.levels;
        const sum = (x: OfficialLevels) => x.pro + x.high + x.medium + x.low;
        const all = sum(L.overall);
        const pct = (n: number) => { const share = rate(n, all); return share === null ? '—' : `${share}%`; };
        // #643 — four levels, the hardest first.
        const tiles: [BoutLevel, string][] = (['pro', 'high', 'medium', 'low'] as const).map(k => [k, levelLabel(t, k)]);
        const seats = (['ring_referee', 'side_judge', 'jury'] as const).filter(s => sum(L[s]) > 0);
        return (
          <div className="card p-4 space-y-3" data-testid="official-levels"
            data-pro={L.overall.pro} data-high={L.overall.high} data-medium={L.overall.medium} data-low={L.overall.low} data-estimated={L.overall.estimated}>
            <SectionTitle title={t.officials.levelTitle} hint={t.officials.levelHint} />
            {/* #631, F3 — one grid for the tiles and the seat rows: each number
                of a seat stands in the column of its tile. The first column
                names the row. */}
            <div className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-x-1.5 sm:gap-x-2 gap-y-1 items-center text-xs" data-testid="official-level-seats">
              <div className="text-gray-500 font-medium pr-1 max-w-[5rem] sm:max-w-none leading-tight">{t.common.total}</div>
              {tiles.map(([key, label]) => (
                <div key={key} data-testid={`official-level-${key}`} className="rounded-lg border border-gray-100 px-1 py-2 sm:p-3 text-center mb-1">
                  <div className="text-2xl font-black tabular-nums">{L.overall[key]}</div>
                  <div className="text-gray-500">{label}</div>
                  <div className="text-gray-400 tabular-nums">{pct(L.overall[key])}</div>
                </div>
              ))}
              {seats.map(seat => (
                <div key={seat} data-testid={`official-level-seat-${seat}`} className="contents">
                  {/* #643 — four tiles on a phone: the name of the row wraps to give them room. */}
                  <div className="py-1 pr-1 max-w-[5rem] sm:max-w-none leading-tight">{seatLabel[seat]}</div>
                  {tiles.map(([key]) => <div key={key} className="py-1 text-center tabular-nums">{L[seat][key]}</div>)}
                </div>
              ))}
            </div>
            {L.overall.unknown > 0 && (
              <p className="text-xs text-gray-500" data-testid="official-level-unknown">{t.officials.levelUnknown.replace('{n}', String(L.overall.unknown))}</p>
            )}
            {L.overall.estimated > 0 && (
              <p className="text-xs text-gray-500" data-testid="official-level-estimates">
                {t.officials.levelEstimates.replace('{n}', String(L.overall.estimated)).replace('{m}', String(all))}
              </p>
            )}
          </div>
        );
      })()}

      {/* #641, #642 — the profile: the team history, the IFMA accreditation
          and the novice status of each seat. The same viewers as the quality numbers. */}
      {data.profile && userId && (
        <OfficialProfile userId={userId} profile={data.profile}
          canEdit={hasRole('admin') || user?.id?.toLowerCase() !== userId.toLowerCase()}
          onChange={profile => {
            // Only the page of the same official takes the answer (a back or a forward during the write).
            const owner = data.official.id;
            setData(d => (d && d.official.id === owner ? { ...d, profile } : d));
            refresh();
          }} />
      )}

      {/* #616, phase 2 — the quality numbers. The server sends them only to
          canSeeAllOfficialStats; the page shows what it received. */}
      {data.quality && (() => {
        const q = data.quality;
        const pct = (a: number, b: number) => { const share = rate(a, b); return share === null ? t.officials.noData : `${share}%`; };
        const of = (a: number, b: number) => t.officials.ofTotal.replace('{a}', String(a)).replace('{b}', String(b));
        type Metric = { key: string; label: string; value: string; detail: string | null; help: string; attrs?: Record<string, string> };
        // #641 — a rate with the minimum of MIN_RATE compared cards.
        const rated = (key: string, label: string, help: string, a: number, b: number): Metric => {
          const shown = b >= MIN_RATE;
          return {
            key, label, help, value: shown ? pct(a, b) : b > 0 ? of(a, b) : t.officials.noData, detail: shown ? of(a, b) : null,
            attrs: { 'data-compared': String(b), 'data-rate': shown ? 'shown' : 'hidden' },
          };
        };
        const r = q.panel.rounds;
        const compared = panelCompared(r);
        const jp = q.jury_panel;
        const metrics: Metric[] = [
          rated('agreement', t.officials.agreement, t.officials.agreementHelp, q.agreement.matched, q.agreement.compared),
          rated('panel', t.officials.panelAgreement, t.officials.panelAgreementHelp, panelWith(r), compared),
          { key: 'dropped', label: t.officials.dropped, value: pct(q.dropped.dropped, q.dropped.rounds),
            detail: q.dropped.rounds > 0 ? of(q.dropped.dropped, q.dropped.rounds) : null, help: t.officials.droppedHelp },
          { key: 'stoppages', label: t.officials.stoppages, value: String(q.stoppages.stoppages),
            detail: q.stoppages.dq > 0 ? `DQ ${q.stoppages.dq}` : null, help: t.officials.stoppagesHelp },
          { key: 'jury', label: t.officials.juryTotals, value: String(q.jury.overrides_differ),
            detail: q.jury.approved_rounds > 0 ? of(q.jury.overrides_differ, q.jury.approved_rounds) : null, help: t.officials.juryTotalsHelp },
          { key: 'jury-panel', label: t.officials.juryPanel, value: String(jp.rounds),
            detail: jp.panel_rounds > 0
              ? t.officials.juryPanelDetail.replace('{a}', String(jp.rounds)).replace('{b}', String(jp.panel_rounds))
                .replace('{c}', String(jp.bouts)).replace('{d}', String(jp.panel_bouts))
              : null,
            help: t.officials.juryPanelHelp, attrs: { 'data-rounds': String(jp.rounds), 'data-bouts': String(jp.bouts) } },
        ];
        return (
          <div className="card p-4 space-y-3" data-testid="official-quality">
            <SectionTitle title={t.officials.quality} hint={t.officials.qualityHint} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {metrics.map(m => (
                <StatTile key={m.key} testId={`official-quality-${m.key}`} attrs={m.attrs} title={m.label} value={m.value} detail={m.detail} help={m.help} />
              ))}
            </div>
            {/* #641 — the panel and the jury: the cells of the cards of a side judge. */}
            {compared > 0 && (
              <div className="space-y-2" data-testid="official-panel">
                <SectionTitle title={t.officials.panelJuryTitle} hint={t.officials.panelJuryHint} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <PanelTable testId="official-panel-rounds" title={t.officials.panelRounds} cells={q.panel.rounds} />
                  <PanelTable testId="official-panel-bouts" title={t.officials.panelBouts} cells={q.panel.bouts} />
                </div>
              </div>
            )}
            {/* #641 — the own-team cards: the team of the judge was in one corner. */}
            {(() => {
              const tq = q.team;
              const own = ownTeamRounds(tq.rounds) + ownTeamRounds(tq.bouts);
              const ownAgainst = ownTeamAgainstJury(tq.rounds);
              const ownRounds = ownTeamRounds(tq.rounds);
              // A judge with no team and no own-team card has nothing to show here.
              if (!own && !data.profile?.team) return null;
              return (
                <div className="space-y-2" data-testid="official-own-team">
                  <SectionTitle title={t.officials.ownTeamTitle} hint={t.officials.ownTeamHint} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <OwnTeamTable testId="official-own-team-rounds" title={t.officials.panelRounds} cells={tq.rounds} />
                    <OwnTeamTable testId="official-own-team-bouts" title={t.officials.panelBouts} cells={tq.bouts} />
                  </div>
                  {/* The bias measure: the own-team rounds against the jury, in each
                      direction (operator, 2026-10-22), beside the other rounds. */}
                  <p className="text-xs text-gray-500" data-testid="official-own-team-reference"
                    data-own-against={ownAgainst}
                    data-own-rounds={ownRounds}
                    data-against={tq.other_against} data-rounds={tq.other_rounds}>
                    {t.officials.ownTeamReference
                      .replace('{n}', String(ownAgainst))
                      .replace('{m}', String(ownRounds))
                      .replace('{a}', String(tq.other_against)).replace('{b}', String(tq.other_rounds))}
                  </p>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* #620 — the numbers of each tournament and the trend. */}
      <OfficialTimeline timeline={data.timeline} trend={data.trend} />

      {/* #631 — the row shell is BoutHistoryList, shared with the athlete
          page. The official row: the seat and the level first (what the page
          is about), then the result in one pill with the winning corner in
          words, then the category. */}
      <BoutHistoryList
        title={t.officials.history} empty={t.officials.noBouts} bouts={bouts} rowTestId="official-bout"
        hint={
          // The info line of the list, as the legend of the bout card
          // (JuryScoringTable): what the counts and the list hold.
          <>
            <p data-testid="official-count-hint" className="px-4 pt-2 pb-1 text-xs text-gray-400">{t.officials.countHint}</p>
            {/* #631 — the legend of the disagreement pills, where the server sent them. */}
            {data.quality && <p data-testid="official-pills-hint" className="px-4 pb-1 text-xs text-gray-400">{t.officials.pillsHint}</p>}
          </>
        }
        corners={b => (
          <>
            {/* A dot and its name never part at a wrap (#631, F6); "vs" goes
                with the blue corner. */}
            <span data-testid="official-bout-red" className="inline-flex items-start gap-1 min-w-0">
              <DotIcon className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
              <span className="min-w-0 break-words">{personName(b.red_first_name, b.red_last_name) || t.common.tbd}</span>
            </span>
            <span data-testid="official-bout-blue" className="inline-flex items-start gap-1 min-w-0">
              <span className="text-gray-400 shrink-0">{t.common.vs}</span>
              <DotIcon className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
              <span className="min-w-0 break-words">{personName(b.blue_first_name, b.blue_last_name) || t.common.tbd}</span>
            </span>
          </>
        )}
        pills={b => (
          <>
            {b.seats.map(s => (
              <span key={s} data-testid="official-bout-seat" className="badge gap-1 bg-indigo-50 text-indigo-700 whitespace-nowrap">{seatIcon[s]}{seatLabel[s]}</span>
            ))}
            {/* #622 — the level of the bout. */}
            {b.level && <BoutLevelPill level={b.level} estimated={b.experience_estimated} />}
            {/* #631 — the disagreements of the official in this bout. */}
            {b.quality && <DisagreementPills q={b.quality} />}
          </>
        )}
        contextPills={b => {
          // The winning corner in words and in its colour, not the colour alone.
          const outcome = outcomeWithRound(b.outcome, b.round_ended);
          if (b.winner === 'red' || b.winner === 'blue') {
            const corner = b.winner === 'red' ? t.common.red : t.common.blue;
            return (
              <span data-testid="official-bout-result" data-winner={b.winner}
                className={`badge whitespace-nowrap ${b.winner === 'red' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                {outcome ? `${corner} · ${outcome}` : corner}
              </span>
            );
          }
          // A draw names the word Draw also with an outcome code (review, 2026-10-21).
          if (b.winner === 'draw') {
            const text = !outcome || b.outcome === 'DRAW' ? (outcome ?? t.common.draw) : `${t.common.draw} · ${outcome}`;
            return <span data-testid="official-bout-result" data-winner="draw" className="badge badge-gray whitespace-nowrap">{text}</span>;
          }
          return null;
        }}
      />
    </div>
  );
}

/** #631 — a mark for each disagreement of the official in one bout. The
 *  marks are: the card gives the other corner of a WP bout; a round outcome
 *  is not the approved one; a card was dropped; a jury total differs from the
 *  suggestion. #641 adds two marks: a dissent that the jury upheld (a good
 *  mark), and a jury decision against the panel. A round with a jury total
 *  says so (operator decision 1). Each mark has a label: a title shows no
 *  text on a touch screen. */
function DisagreementPills({ q }: { q: OfficialBoutQuality }) {
  const { t } = useI18n();
  const rounds = (list: number[]) => list.map(n => `R${n}`).join(', ');
  const cls = 'badge gap-1 bg-amber-50 text-amber-800 whitespace-nowrap';
  const icon = <WarningIcon className="w-3.5 h-3.5 shrink-0" />;
  // #641 — the rounds, each with the jury-total mark, and the bout.
  const what = (list: number[], bout: boolean) => [
    ...list.map(n => q.rounds_jury_total.includes(n) ? t.officials.pillJuryTotalRound.replace('{round}', `R${n}`) : `R${n}`),
    ...(bout ? [t.officials.pillBout] : []),
  ].join(', ');
  return (
    <>
      {/* #641 — the team of the judge is in a corner, and the card is better
          or worse for that corner than the jury decision: both are marks. */}
      {q.own_corner && (
        <span data-testid="official-bout-own-team" data-corner={q.own_corner} className="badge gap-1 bg-gray-100 text-gray-700 whitespace-nowrap">
          {t.officials.pillOwnTeam.replace('{corner}', q.own_corner === 'red' ? t.common.red : t.common.blue)}
        </span>
      )}
      {(q.rounds_for_own.length > 0 || q.bout_for_own) && (
        <span data-testid="official-bout-for-own" data-rounds={q.rounds_for_own.join(',')} data-bout={String(q.bout_for_own)}
          className="badge gap-1 bg-amber-50 text-amber-800 max-w-full whitespace-normal text-left">
          {icon}{t.officials.pillForOwn.replace('{what}', what(q.rounds_for_own, q.bout_for_own))}
        </span>
      )}
      {(q.rounds_against_own.length > 0 || q.bout_against_own) && (
        <span data-testid="official-bout-against-own" data-rounds={q.rounds_against_own.join(',')} data-bout={String(q.bout_against_own)}
          className="badge gap-1 bg-amber-50 text-amber-800 max-w-full whitespace-normal text-left">
          {icon}{t.officials.pillAgainstOwn.replace('{what}', what(q.rounds_against_own, q.bout_against_own))}
        </span>
      )}
      {(q.rounds_dissent_upheld.length > 0 || q.bout_dissent_upheld) && (
        <span data-testid="official-bout-dissent-upheld" data-rounds={q.rounds_dissent_upheld.join(',')} data-bout={String(q.bout_dissent_upheld)}
          className="badge gap-1 bg-emerald-50 text-emerald-700 max-w-full whitespace-normal text-left">
          <CheckIcon className="w-3.5 h-3.5 shrink-0" />{t.officials.pillDissentUpheld.replace('{what}', what(q.rounds_dissent_upheld, q.bout_dissent_upheld))}
        </span>
      )}
      {(q.jury_rounds_against_panel.length > 0 || q.jury_bout_against_panel) && (
        <span data-testid="official-bout-against-panel" data-rounds={q.jury_rounds_against_panel.join(',')} data-bout={String(q.jury_bout_against_panel)}
          className="badge gap-1 bg-sky-50 text-sky-800 max-w-full whitespace-normal text-left">
          <ScaleIcon className="w-3.5 h-3.5 shrink-0" />{t.officials.pillAgainstPanel.replace('{what}', what(q.jury_rounds_against_panel, q.jury_bout_against_panel))}
        </span>
      )}
      {q.card_other_corner && (
        <span data-testid="official-bout-disagree-card" data-corner={q.card_other_corner} className={cls}>
          {icon}{t.officials.pillCard.replace('{corner}', q.card_other_corner === 'red' ? t.common.red : t.common.blue)}
        </span>
      )}
      {q.rounds_disagree.length > 0 && (
        <span data-testid="official-bout-disagree-rounds" data-rounds={q.rounds_disagree.join(',')} className={cls}>
          {icon}{t.officials.pillRounds.replace('{rounds}', rounds(q.rounds_disagree))}
        </span>
      )}
      {q.rounds_dropped.length > 0 && (
        <span data-testid="official-bout-disagree-dropped" data-rounds={q.rounds_dropped.join(',')} className={cls}>
          {icon}{t.officials.pillDropped.replace('{rounds}', rounds(q.rounds_dropped))}
        </span>
      )}
      {q.jury_rounds_differ.length > 0 && (
        <span data-testid="official-bout-disagree-jury" data-rounds={q.jury_rounds_differ.join(',')} className={cls}>
          {icon}{t.officials.pillJury.replace('{rounds}', rounds(q.jury_rounds_differ))}
        </span>
      )}
    </>
  );
}
